import React from 'react';

import { ChartCard } from '@/design-system/chart-card';
import { ComboChart } from '@/design-system/charts';
import type {
  DashboardTrend,
  MetricStatus,
} from '@/services/api/dashboard';

import {
  fmtSecPerItem,
  fmtTrendDelta,
  shortDate,
  trendTone,
} from './format';

interface VelocityDayLike {
  date: string;
  itemCount: number;
  secPerItem: number | null;
  maSecPerItem: number | null;
}

interface VelocityTrendCardProps {
  eyebrow: string;
  title: string;
  subtitle?: string;
  status: MetricStatus;
  currentMa: number | null;
  trend: DashboardTrend;
  days: VelocityDayLike[];
  loading: boolean;
  /** Optional stat strip under the chart (e.g. rider arrival lag). */
  footer?: React.ReactNode;
}

/**
 * One stage-velocity panel: per-day sec/item bars with the 7-day MA line
 * (standing decision: the MA is the trend; daily bars are context). Ink
 * bars, accent line — GSM color-as-punctuation.
 */
const VelocityTrendCard: React.FC<VelocityTrendCardProps> = ({
  eyebrow,
  title,
  subtitle,
  status,
  currentMa,
  trend,
  days,
  loading,
  footer,
}) => {
  const data = days.map((d) => ({
    date: shortDate(d.date),
    secPerItem: d.secPerItem,
    ma: d.maSecPerItem,
    items: d.itemCount,
  }));
  const hasAny = days.some((d) => d.secPerItem != null);
  const state = loading
    ? 'loading'
    : status !== 'OK' || !hasAny
      ? 'empty'
      : 'ready';

  return (
    <ChartCard
      eyebrow={eyebrow}
      title={title}
      subtitle={subtitle}
      value={fmtSecPerItem(currentMa)}
      delta={{
        value: fmtTrendDelta(trend),
        trend: trendTone(trend.direction),
      }}
      state={state}
      emptyMessage={
        status === 'UNAVAILABLE'
          ? 'Metric unavailable'
          : 'Not enough data in this window'
      }
    >
      <ComboChart
        data={data}
        xKey="date"
        bars={[{ key: 'secPerItem', label: 'sec/item' }]}
        lines={[{ key: 'ma', label: '7-day MA' }]}
        height={180}
      />
      {footer}
    </ChartCard>
  );
};

export default VelocityTrendCard;
